// A small, safe Markdown renderer that builds React elements directly.
// Model output is never turned into HTML: every character lands in a text
// node, so a "<script>" in a draft renders as the literal text "<script>".
//
// Supported: ATX headings, paragraphs (their line breaks kept), bold, italic, inline code, fenced
// code blocks, unordered and ordered lists (nested by indentation), pipe
// tables with alignment, blockquotes, horizontal rules, and http(s) links.
// Anything else stays readable as plain text.
import { Fragment, type ReactNode } from "react";

type Align = "left" | "center" | "right" | null;

export type Block =
  | { kind: "heading"; level: number; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "code"; lang: string; text: string }
  | { kind: "hr" }
  | { kind: "quote"; blocks: Block[] }
  | { kind: "list"; ordered: boolean; start: number; items: Block[][] }
  | { kind: "table"; header: string[]; align: Align[]; rows: string[][] };

const FENCE = /^\s{0,3}(`{3,}|~{3,})\s*([\w-]*)\s*$/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.*?)(?:\s+#+)?\s*$/;
const HR = /^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/;
const QUOTE = /^\s{0,3}>\s?(.*)$/;
const LIST_ITEM = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const TABLE_SEP = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

function indentOf(line: string): number {
  const m = /^(\s*)/.exec(line);
  return m ? m[1]!.replace(/\t/g, "    ").length : 0;
}

function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  return s.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, "|"));
}

function alignOf(cell: string): Align {
  const c = cell.trim();
  const left = c.startsWith(":");
  const right = c.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  if (left) return "left";
  return null;
}

function startsBlock(line: string, next: string | undefined): boolean {
  return (
    FENCE.test(line) ||
    HEADING.test(line) ||
    HR.test(line) ||
    QUOTE.test(line) ||
    LIST_ITEM.test(line) ||
    (line.includes("|") && next !== undefined && TABLE_SEP.test(next) && next.includes("-"))
  );
}

export function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  return parseLines(lines);
}

function parseLines(lines: string[]): Block[] {
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }

    const fence = FENCE.exec(line);
    if (fence) {
      const marker = fence[1]!;
      const body: string[] = [];
      i++;
      while (i < lines.length && !new RegExp(`^\\s{0,3}${marker[0] === "`" ? "`" : "~"}{${marker.length},}\\s*$`).test(lines[i]!)) {
        body.push(lines[i]!);
        i++;
      }
      i++; // closing fence (or end of input)
      blocks.push({ kind: "code", lang: fence[2] ?? "", text: body.join("\n") });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ kind: "heading", level: heading[1]!.length, text: heading[2] ?? "" });
      i++;
      continue;
    }

    if (HR.test(line)) {
      blocks.push({ kind: "hr" });
      i++;
      continue;
    }

    const next = lines[i + 1];
    if (line.includes("|") && next !== undefined && TABLE_SEP.test(next) && next.includes("-")) {
      const header = splitRow(line);
      const align = splitRow(next).map(alignOf);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i]!.trim() && lines[i]!.includes("|")) {
        const cells = splitRow(lines[i]!);
        rows.push(header.map((_, c) => cells[c] ?? ""));
        i++;
      }
      blocks.push({ kind: "table", header, align: header.map((_, c) => align[c] ?? null), rows });
      continue;
    }

    if (QUOTE.test(line)) {
      const inner: string[] = [];
      while (i < lines.length && lines[i]!.trim()) {
        const q = QUOTE.exec(lines[i]!);
        if (q) inner.push(q[1] ?? "");
        else if (!startsBlock(lines[i]!, lines[i + 1])) inner.push(lines[i]!);
        else break;
        i++;
      }
      blocks.push({ kind: "quote", blocks: parseLines(inner) });
      continue;
    }

    const item = LIST_ITEM.exec(line);
    if (item) {
      const baseIndent = indentOf(line);
      const ordered = /\d/.test(item[2]!);
      const start = ordered ? parseInt(item[2]!, 10) : 1;
      const items: Block[][] = [];
      while (i < lines.length) {
        const m = LIST_ITEM.exec(lines[i]!);
        if (!m || indentOf(lines[i]!) !== baseIndent || /\d/.test(m[2]!) !== ordered) break;
        const content: string[] = [m[3] ?? ""];
        const contentIndent = baseIndent + m[2]!.length + 1;
        i++;
        while (i < lines.length) {
          const l = lines[i]!;
          if (!l.trim()) {
            // A blank line ends the item unless indented content follows.
            const after = lines[i + 1];
            if (after !== undefined && after.trim() && indentOf(after) > baseIndent) {
              content.push("");
              i++;
              continue;
            }
            break;
          }
          const ind = indentOf(l);
          if (ind > baseIndent) {
            content.push(l.replace(/\t/g, "    ").slice(Math.min(ind, contentIndent)));
            i++;
            continue;
          }
          if (LIST_ITEM.test(l) || startsBlock(l, lines[i + 1])) break;
          content.push(l.trim()); // lazy continuation
          i++;
        }
        items.push(parseLines(content));
        // Skip the blank line between items of one loose list.
        if (i < lines.length && !lines[i]!.trim()) {
          const after = lines[i + 1];
          const am = after !== undefined ? LIST_ITEM.exec(after) : null;
          if (am && indentOf(after!) === baseIndent && /\d/.test(am[2]!) === ordered) i++;
        }
      }
      blocks.push({ kind: "list", ordered, start, items });
      continue;
    }

    const para: string[] = [line.trim()];
    i++;
    while (i < lines.length && lines[i]!.trim() && !startsBlock(lines[i]!, lines[i + 1])) {
      para.push(lines[i]!.trim());
      i++;
    }
    // Line breaks inside a paragraph are kept: "Nama Paket: ...", "Instansi: ..."
    // written one per line must not run together.
    blocks.push({ kind: "paragraph", text: para.join("\n") });
  }
  return blocks;
}

// ---------------------------------------------------------------------------
// Inline
// ---------------------------------------------------------------------------
interface InlineRule {
  re: RegExp;
  render: (m: RegExpExecArray, key: string) => ReactNode;
  /** Characters of the match that belong to the text before the element. */
  lead?: (m: RegExpExecArray) => string;
}

const SAFE_URL = /^https?:\/\/[^\s<>"']+$/i;

const RULES: InlineRule[] = [
  { re: /\\([\\`*_{}[\]()#+\-.!|>~])/, render: (m) => m[1] },
  { re: /`([^`]+)`/, render: (m, k) => <code key={k}>{m[1]}</code> },
  { re: /\*\*(?=\S)([\s\S]*?\S)\*\*/, render: (m, k) => <strong key={k}>{inline(m[1]!, k)}</strong> },
  { re: /__(?=\S)([\s\S]*?\S)__(?!\w)/, render: (m, k) => <strong key={k}>{inline(m[1]!, k)}</strong> },
  {
    re: /\[([^\]\n]+)\]\(([^)\s]+)\)/,
    render: (m, k) =>
      SAFE_URL.test(m[2]!) ? (
        <a key={k} href={m[2]} rel="noopener noreferrer" target="_blank">
          {inline(m[1]!, k)}
        </a>
      ) : (
        <Fragment key={k}>{inline(m[1]!, k)}</Fragment>
      ),
  },
  { re: /\*(?=\S)([^*]*?\S)\*/, render: (m, k) => <em key={k}>{inline(m[1]!, k)}</em> },
  {
    re: /(^|[^\w])_(?=\S)([^_]*?\S)_(?!\w)/,
    lead: (m) => m[1] ?? "",
    render: (m, k) => <em key={k}>{inline(m[2]!, k)}</em>,
  },
];

export function inline(text: string, keyBase = "i"): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;
  while (rest) {
    let best: { rule: InlineRule; m: RegExpExecArray } | null = null;
    for (const rule of RULES) {
      const m = rule.re.exec(rest);
      if (m && (best === null || m.index < best.m.index)) best = { rule, m };
    }
    if (!best) {
      out.push(rest);
      break;
    }
    const lead = best.rule.lead?.(best.m) ?? "";
    const before = rest.slice(0, best.m.index) + lead;
    if (before) out.push(before);
    out.push(best.rule.render(best.m, `${keyBase}.${n++}`));
    rest = rest.slice(best.m.index + best.m[0].length);
  }
  return out;
}

/** A paragraph's lines, each through `inline`, with a <br> between them. */
function lines(text: string, keyBase: string): ReactNode[] {
  return text.split("\n").flatMap((line, i) => {
    const parts = inline(line, `${keyBase}.l${i}`);
    return i === 0 ? parts : [<br key={`${keyBase}.br${i}`} />, ...parts];
  });
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------
type HeadingTag = "h2" | "h3" | "h4" | "h5" | "h6";

function renderBlocks(blocks: Block[], offset: number, key: string, tight = false): ReactNode[] {
  return blocks.map((b, idx) => {
    const k = `${key}.${idx}`;
    switch (b.kind) {
      case "heading": {
        const level = Math.min(6, Math.max(2, b.level + offset));
        const Tag = `h${level}` as HeadingTag;
        return <Tag key={k}>{inline(b.text, k)}</Tag>;
      }
      case "paragraph":
        return tight ? <Fragment key={k}>{lines(b.text, k)}</Fragment> : <p key={k}>{lines(b.text, k)}</p>;
      case "code":
        return (
          <pre key={k} className="md-code">
            <code>{b.text}</code>
          </pre>
        );
      case "hr":
        return <hr key={k} />;
      case "quote":
        return <blockquote key={k}>{renderBlocks(b.blocks, offset, k)}</blockquote>;
      case "list": {
        const items = b.items.map((item, j) => {
          const single = item.length === 1 && item[0]!.kind === "paragraph";
          return <li key={`${k}.${j}`}>{renderBlocks(item, offset, `${k}.${j}`, single)}</li>;
        });
        return b.ordered ? (
          <ol key={k} start={b.start !== 1 ? b.start : undefined}>
            {items}
          </ol>
        ) : (
          <ul key={k}>{items}</ul>
        );
      }
      case "table":
        return (
          <div key={k} className="md-table scroll-x" role="region" aria-label="Tabel" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  {b.header.map((h, c) => (
                    <th key={c} scope="col" style={b.align[c] ? { textAlign: b.align[c]! } : undefined}>
                      {inline(h, `${k}.h${c}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td key={c} style={b.align[c] ? { textAlign: b.align[c]! } : undefined}>
                        {inline(cell, `${k}.${r}.${c}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
    }
  });
}

export interface MarkdownProps {
  source: string;
  /** Added to every heading level; the page owns its h1, so a "#" renders as h2. */
  headingOffset?: number;
  className?: string;
}

export function Markdown({ source, headingOffset = 1, className }: MarkdownProps) {
  const blocks = parseBlocks(source);
  return <div className={className ? `md ${className}` : "md"}>{renderBlocks(blocks, headingOffset, "b")}</div>;
}
